/* eslint-disable react-refresh/only-export-components -- Route configuration intentionally owns its lazy route wrappers. */
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { PublicLayout } from '@/components/layout/public';
import { AccountLayout } from '@/components/layout/account';
import { AdminLayout } from '@/components/layout/admin';
import { AuthLayout } from '@/components/layout/auth';
import { GuestOnlyRoute, RequireAuth, RequireRole } from '@/features/auth/components';
import {
  DashboardPage,
  ForgotPasswordPage,
  LoginPage,
  LogoutPage,
  OrdersPage,
  RegisterPage,
  ResetPasswordPage,
  UnauthorizedPage,
  VerifyEmailPage,
  WorkshopPage,
} from '@/features/auth/pages';
import { routes as appRoutes } from '@/config/routes';
import { CategoryPage } from '@/features/catalog/pages/CategoryPage';
import { ProductDetailPage } from '@/features/catalog/pages/ProductDetailPage';
import { ProductsPage } from '@/features/catalog/pages/ProductsPage';
import { SearchPage } from '@/features/catalog/pages/SearchPage';
import {
  CommunicationPage,
  ConsentHistoryPage,
  ProfilePage,
  SecurityPage,
} from '@/features/account/pages';
import { HomePage } from '@/pages/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PlaceholderPage } from '@/pages/PlaceholderPage';

const AboutPage = lazy(() =>
  import('@/pages/AboutPage').then((module) => ({ default: module.AboutPage })),
);
const ContactPage = lazy(() =>
  import('@/pages/ContactPage').then((module) => ({ default: module.ContactPage })),
);
const FaqPage = lazy(() =>
  import('@/pages/FaqPage').then((module) => ({ default: module.FaqPage })),
);
const HowItWorksPage = lazy(() =>
  import('@/pages/HowItWorksPage').then((module) => ({ default: module.HowItWorksPage })),
);
const LegalPage = lazy(() =>
  import('@/pages/LegalPage').then((module) => ({ default: module.LegalPage })),
);
const AdminCatalogOverviewPage = lazy(() =>
  import('@/features/admin/catalog/pages').then((module) => ({
    default: module.AdminCatalogOverviewPage,
  })),
);
const AdminCategoryListPage = lazy(() =>
  import('@/features/admin/catalog/pages').then((module) => ({
    default: module.AdminCategoryListPage,
  })),
);
const AdminCategoryFormPage = lazy(() =>
  import('@/features/admin/catalog/pages').then((module) => ({
    default: module.AdminCategoryFormPage,
  })),
);
const AdminProductListPage = lazy(() =>
  import('@/features/admin/catalog/pages').then((module) => ({
    default: module.AdminProductListPage,
  })),
);
const AdminProductFormPage = lazy(() =>
  import('@/features/admin/catalog/pages').then((module) => ({
    default: module.AdminProductFormPage,
  })),
);
const AdminAuditPage = lazy(() =>
  import('@/features/admin/catalog/pages').then((module) => ({ default: module.AdminAuditPage })),
);
const AdminCatalogMediaPage = lazy(() =>
  import('@/features/admin/catalog/media/pages').then((module) => ({
    default: module.AdminCatalogMediaPage,
  })),
);

function RouteLoadingState() {
  return (
    <div className="route-loading" role="status">
      <span className="wordmark__mark" aria-hidden="true">
        E
      </span>
      <span>Preparing something personal…</span>
    </div>
  );
}

function deferred(element: ReactNode) {
  return <Suspense fallback={<RouteLoadingState />}>{element}</Suspense>;
}

const placeholder = (title: string, description: string) => (
  <PlaceholderPage title={title} description={description} />
);

export const routes: RouteObject[] = [
  {
    path: appRoutes.home,
    element: <PublicLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'products', element: <ProductsPage /> },
      { path: 'products/:categorySlug', element: <CategoryPage /> },
      { path: 'products/:categorySlug/:productSlug', element: <ProductDetailPage /> },
      { path: 'search', element: <SearchPage /> },
      { path: 'about', element: deferred(<AboutPage />) },
      { path: 'how-it-works', element: deferred(<HowItWorksPage />) },
      { path: 'contact', element: deferred(<ContactPage />) },
      { path: 'faq', element: deferred(<FaqPage />) },
      { path: 'privacy', element: deferred(<LegalPage kind="privacy" />) },
      { path: 'terms', element: deferred(<LegalPage kind="terms" />) },
      {
        path: 'dashboard',
        element: (
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        ),
      },
      { path: 'checkout', element: placeholder('Checkout', 'Secure checkout is planned for M7.') },
      {
        path: 'orders',
        element: (
          <RequireAuth>
            <OrdersPage />
          </RequireAuth>
        ),
      },
      {
        path: 'account',
        element: (
          <RequireAuth>
            <AccountLayout />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <Navigate to={appRoutes.accountProfile} replace /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'communication', element: <CommunicationPage /> },
          { path: 'security', element: <SecurityPage /> },
          { path: 'consents', element: <ConsentHistoryPage /> },
        ],
      },
      {
        path: 'workshop',
        element: (
          <RequireRole allowedRoles={['admin', 'workshop']}>
            <WorkshopPage />
          </RequireRole>
        ),
      },
      { path: 'logout', element: <LogoutPage /> },
      { path: 'unauthorized', element: <UnauthorizedPage /> },
      { path: 'not-found', element: <NotFoundPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: appRoutes.admin,
    element: (
      <RequireRole allowedRoles={['admin']}>
        <AdminLayout />
      </RequireRole>
    ),
    children: [
      { index: true, element: <Navigate to={appRoutes.adminCatalog} replace /> },
      { path: 'catalog', element: deferred(<AdminCatalogOverviewPage />) },
      { path: 'catalog/categories', element: deferred(<AdminCategoryListPage />) },
      { path: 'catalog/categories/new', element: deferred(<AdminCategoryFormPage create />) },
      { path: 'catalog/categories/:categoryId', element: deferred(<AdminCategoryFormPage />) },
      {
        path: 'catalog/categories/:categoryId/audit',
        element: deferred(<AdminAuditPage kind="category" />),
      },
      { path: 'catalog/products', element: deferred(<AdminProductListPage />) },
      { path: 'catalog/media', element: deferred(<AdminCatalogMediaPage />) },
      { path: 'catalog/products/new', element: deferred(<AdminProductFormPage create />) },
      { path: 'catalog/products/:productId', element: deferred(<AdminProductFormPage />) },
      {
        path: 'catalog/products/:productId/audit',
        element: deferred(<AdminAuditPage kind="product" />),
      },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      {
        path: appRoutes.login,
        element: (
          <GuestOnlyRoute>
            <LoginPage />
          </GuestOnlyRoute>
        ),
      },
      {
        path: appRoutes.register,
        element: (
          <GuestOnlyRoute>
            <RegisterPage />
          </GuestOnlyRoute>
        ),
      },
      { path: appRoutes.verifyEmail, element: <VerifyEmailPage /> },
      { path: appRoutes.forgotPassword, element: <ForgotPasswordPage /> },
      { path: appRoutes.resetPassword, element: <ResetPasswordPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
